import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, theme } from 'antd';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';
import {
    IS_MANUAL_PUSH_WAREHOUSE,
    IsManualPushWarehouseResponse,
    IsManualPushWarehouseVariables,
} from '../../../../graphql/queries/isManualPushWarehouse';
import { GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse } from '../../../../graphql/queries/warehouseQueries';
import { buildParcelSlaMonitoringUrl, warehouseLocalDate } from '../../../../utils/parcelSlaMonitoringLink';

interface ManualPushHintProps {
    warehouseId?: number;
}

/**
 * 线路页强提示（分批次-尾程，只在 DSP 视角渲染，仓管视角不展示）：按接口判断仓库为手动推送模式时展示 PRD 提示文案；
 * 查询失败或非手动模式时不展示。「历史包裹追踪」跳转 DSP 侧历史包裹追踪（当前仓、仓库当地当天、「到达HUB时间」视图）。
 *
 * 样式同批次推送页横幅：底色、边框同「查看已选明细」汇总条（amber-50 底 / amber-200 边，warning 图标），
 * 「历史包裹追踪」为主题链接色（同 Typography.Link「点击查看示例」）、不加粗。
 * 仓库时区按 id 查 getWarehouseById 取 timezoneLocation（与线路模板预测派送量同口径），取不到时只展示文字、不跳转。
 */
const ManualPushHint: React.FC<ManualPushHintProps> = ({ warehouseId }) => {
    const { data } = useQuery<IsManualPushWarehouseResponse, IsManualPushWarehouseVariables>(IS_MANUAL_PUSH_WAREHOUSE, {
        variables: { warehouseId: Number(warehouseId) },
        skip: !warehouseId,
        fetchPolicy: 'cache-and-network',
    });
    const manual = !!data?.manual;
    const { token } = theme.useToken();
    const { data: warehouseData } = useQuery<GetWarehouseByIdResponse>(GET_WAREHOUSE_BY_ID, {
        variables: { id: warehouseId },
        skip: !warehouseId || !manual,
    });
    if (!manual || !warehouseId) return null;
    const localDate = warehouseLocalDate(warehouseData?.warehouse?.timezoneLocation);
    // 标签名不能用 link：它是 HTML 空元素，i18next 解析时会当成自闭合，文字落到链接外
    const jump = localDate
        ? <Link
            to={buildParcelSlaMonitoringUrl(warehouseId, localDate, true)}
            style={{ color: token.colorLink }}
            data-testid="manual-push-hint-link"
        />
        : <span />;
    return (
        <Alert
            type="warning"
            showIcon
            className="mb-4 !bg-amber-50 !border-amber-200"
            data-testid="manual-push-hint"
            message={
                <span>
                    <Trans i18nKey="delivery.routes.message.manualPushHint" components={{ jump }} />
                </span>
            }
        />
    );
};

export default ManualPushHint;
